import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnChanges,
  Output,
} from "@angular/core";
import { Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { HeicConverterService } from "src/app/shared/services/heic-converter.service";
import { UploadObsService } from "src/app/shared/services/upload-obs.service";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import Swal from "sweetalert2";

/** Virtual scroll: row height (px) - image 109px + padding + filename */
const PHOTO_ROW_HEIGHT = 150;
/** Columns in grid - match col-md-3 (4 per row) */
const PHOTO_GRID_COLUMNS = 4;
/** Use virtual scroll when media count exceeds this */
const VIRTUAL_SCROLL_THRESHOLD = 30;

@Component({
  selector: "app-tab-photos",
  templateUrl: "./tab-photos.component.html",
  styleUrls: ["./tab-photos.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabPhotosComponent implements OnChanges, OnDestroy {
  // Constants for limits
  private readonly MAX_IMAGES_PER_SECTION = 1000;
  private readonly MAX_IMAGE_SIZE_MB = 20;
  private readonly MAX_SUBSECTIONS_PER_SECTION = 12;

  // Track pending uploads per section
  private pendingUploadsPerSection: { [sectionIndex: number]: number } = {};
  private activeUploads = new Map<string, Subscription>();

  // Getter methods to expose constants to template
  get maxImagesPerSection(): number {
    return this.MAX_IMAGES_PER_SECTION;
  }

  get maxImageSizeMB(): number {
    return this.MAX_IMAGE_SIZE_MB;
  }

  get maxSubsectionsPerSection(): number {
    return this.MAX_SUBSECTIONS_PER_SECTION;
  }

  @Input() model: any = {};
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;
  @Input() uploadProgress: { [key: string]: number } = {};
  @Input() uploadStates: { [key: string]: boolean } = {};
  @Input() allPhotoSectionsExpanded: boolean = false;
  @Input() deleteSection: any[] = [];
  @Input() deleteSubSection: any[] = [];
  @Input() deleteMedia: any[] = [];

  @Output() modelChange = new EventEmitter<any>();
  @Output() autoCheckEvent = new EventEmitter<string>();
  @Output() deleteSectionChange = new EventEmitter<any[]>();
  @Output() deleteSubSectionChange = new EventEmitter<any[]>();
  @Output() deleteMediaChange = new EventEmitter<any[]>();

  readonly photoItemSize = PHOTO_ROW_HEIGHT;
  readonly virtualScrollThreshold = VIRTUAL_SCROLL_THRESHOLD;

  constructor(
    private toastr: ToastrService,
    private uploadService: UploadObsService,
    private heicConverter: HeicConverterService,
    private cdr: ChangeDetectorRef
  ) {}
  ngOnInit(): void {
    if (this.model.photoSections && this.model.photoSections.length > 0) {
      this.model.photoSections.forEach((section, index) => {
        section.collapsed = false;
        if (section.display === undefined) {
          section.display = true;
        }
        if (section.index === undefined) {
          section.index = index;
        }
        if (section.subSections) {
          section.subSections.forEach((subsection, subIndex) => {
            subsection.collapsed = false;
            if (subsection.display === undefined) {
              subsection.display = true;
            }
            if (subsection.index === undefined) {
              subsection.index = subIndex;
            }
          });
        }
      });

      // Cập nhật checkbox chính dựa trên trạng thái của các sections sau khi khởi tạo
      this.updateMainDisplayCheckbox();
    } else {
      // Nếu không có section nào:
      // - Nếu user đã check displayPhotos = true, giữ nguyên để validation có thể báo lỗi
      // - Chỉ untick nếu displayPhotos = false (chưa được check)
      if (this.model.displayPhotos === false) {
        this.model.displayPhotos = false;
      }
      // Nếu displayPhotos = true (user đã check), giữ nguyên để validation báo lỗi khi publish
    }
    this.allPhotoSectionsExpanded = true;
  }
  checkAndSetPhotoDisplay() {
    if (!this.model.displayPhotos) {
      if (
        this.model.titlePhotoSections ||
        (this.model.photoSections &&
          this.model.photoSections.length > 0 &&
          this.model.photoSections.some(
            section =>
              section.name ||
              (section.subSections &&
                section.subSections.some(
                  sub => sub.title || sub.pageTitle || sub.description || (sub.media && sub.media.length > 0)
                ))
          ))
      ) {
        this.model.displayPhotos = true;
      }
    }
    this.modelChange.emit(this.model);
  }

  addPhotoSection() {
    if (!this.model.photoSections) {
      this.model.photoSections = [];
    }
    const nextSectionIndex =
      this.model.photoSections.length === 0 ? 0 : Math.max(...this.model.photoSections.map(s => s.index || 0)) + 1;
    const inheritDisplay = !!this.model.displayPhotos;
    const newSection = {
      name: "",
      collapsed: false,
      display: inheritDisplay,
      index: nextSectionIndex,
      subSections: [
        {
          title: "",
          pageTitle: "",
          description: "",
          media: [],
          collapsed: false,
          display: inheritDisplay,
          index: 0,
        },
      ],
    };
    this.model.photoSections.push(newSection);

    // this.allPhotoSectionsExpanded = true;
    // this.model.photoSections.forEach(section => {
    //   section.collapsed = false;
    //   if (section.subSections) {
    //     section.subSections.forEach(subsection => {
    //       subsection.collapsed = false;
    //     });
    //   }
    // });

    // Cập nhật checkbox chính dựa trên trạng thái của các sections
    this.updateMainDisplayCheckbox();
    this.modelChange.emit(this.model);
  }

  deletePhotoSection(sectionIndex: number) {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the entire section and all its content.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        const deleSection = this.model.photoSections[sectionIndex];
        if (deleSection && deleSection.id) {
          this.deleteSection.push(deleSection.id);
          this.deleteSectionChange.emit(this.deleteSection);
        }
        this.model.photoSections.splice(sectionIndex, 1);
        this.cdr.markForCheck();
        this.modelChange.emit(this.model);
      }
    });
  }

  addPhotoSubsection(sectionIndex: number) {
    const section = this.model.photoSections[sectionIndex];
    if (section.subSections.length >= this.MAX_SUBSECTIONS_PER_SECTION) {
      this.toastr.error(`Maximum ${this.MAX_SUBSECTIONS_PER_SECTION} sub-sections allowed per section`);
      return;
    }

    // if (this.isPhotoSectionAtMaxCapacity(sectionIndex)) {
    //   const currentPhotos = this.getPhotoCountInSection(sectionIndex);
    //   const pendingUploads = this.pendingUploadsPerSection[sectionIndex] || 0;
    //   const totalCount = currentPhotos + pendingUploads;

    //   this.toastr.error(
    //     `Maximum ${this.MAX_IMAGES_PER_SECTION} images per section reached. ` +
    //     `Current total: ${totalCount} (${currentPhotos} uploaded + ${pendingUploads} pending). ` +
    //     `Cannot add more sub-sections.`
    //   );
    //   return;
    // }

    const nextSubsectionIndex =
      section.subSections.length === 0 ? 0 : Math.max(...section.subSections.map(s => s.index || 0)) + 1;
    section.subSections.push({
      title: "",
      pageTitle: "",
      description: "",
      media: [],
      collapsed: false,
      display: !!section.display,
      index: nextSubsectionIndex,
    });

    // Cập nhật checkbox chính dựa trên trạng thái của các sections
    this.updateMainDisplayCheckbox();
    this.modelChange.emit(this.model);
  }

  deletePhotoSubsection(sectionIndex: number, subIndex: number) {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the subsection and all its content.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        const deleSubSection = this.model.photoSections[sectionIndex].subSections[subIndex];
        if (deleSubSection && deleSubSection.id) {
          this.deleteSubSection.push(deleSubSection.id);
          this.deleteSubSectionChange.emit(this.deleteSubSection);
        }
        this.model.photoSections[sectionIndex].subSections.splice(subIndex, 1);
        this.cdr.markForCheck();
        this.modelChange.emit(this.model);
      }
    });
  }

  togglePhotoSectionCollapse(section: any) {
    section.collapsed = !section.collapsed;
  }

  togglePhotoSubsectionCollapse(subsection: any) {
    subsection.collapsed = !subsection.collapsed;
  }

  onDisplayPhotosChange(value: boolean) {
    this.model.displayPhotos = value;
    // Tick/untick tất cả sections và subsections
    if (this.model.photoSections && this.model.photoSections.length > 0) {
      this.model.photoSections.forEach(section => {
        section.display = value;
        if (section.subSections) {
          section.subSections.forEach(subsection => {
            subsection.display = value;
          });
        }
      });
    }
    this.modelChange.emit(this.model);
  }

  onSectionDisplayChange(sectionIndex: number, value: boolean) {
    const section = this.model.photoSections[sectionIndex];
    if (section) {
      section.display = value;

      // Tick/untick tất cả subsection trong section này
      if (section.subSections) {
        section.subSections.forEach(subsection => {
          subsection.display = value;
        });
      }

      // Tự động tick/untick checkbox chính dựa trên trạng thái của các sections
      const oldDisplayPhotos = this.model.displayPhotos;
      this.updateMainDisplayCheckbox();

      this.modelChange.emit(this.model);
    }
  }

  onSubsectionDisplayChange(sectionIndex: number, subIndex: number, value: boolean) {
    const section = this.model.photoSections[sectionIndex];
    if (section && section.subSections && section.subSections[subIndex]) {
      section.subSections[subIndex].display = value;

      // Nếu subsection được chọn, tự động chọn section
      if (value) {
        section.display = true;
      } else {
        // Nếu subsection bị unselect, kiểm tra xem còn subsection nào được chọn không
        const hasAnySubsectionSelected = section.subSections.some(sub => sub.display === true);
        // Nếu không còn subsection nào được chọn, unselect section
        if (!hasAnySubsectionSelected) {
          section.display = false;
        }
      }

      // Tự động tick/untick checkbox chính dựa trên trạng thái của các sections
      const oldDisplayPhotos = this.model.displayPhotos;
      this.updateMainDisplayCheckbox();

      this.modelChange.emit(this.model);
    }
  }

  /**
   * Cập nhật checkbox chính (displayPhotos) dựa trên trạng thái của các sections
   * - Nếu có ít nhất 1 section được tick → tick checkbox chính
   * - Nếu tất cả sections đều untick → chỉ untick checkbox chính nếu user chưa check
   *   (để tránh override khi user đã manually check nhưng chưa có sections)
   */
  private updateMainDisplayCheckbox() {
    if (!this.model.photoSections || this.model.photoSections.length === 0) {
      // Nếu không có section nào:
      // - Nếu user đã check displayPhotos = true, giữ nguyên để validation có thể báo lỗi
      // - Chỉ untick nếu displayPhotos = false (chưa được check)
      if (this.model.displayPhotos === false) {
        this.model.displayPhotos = false;
      }
      // Nếu displayPhotos = true (user đã check), giữ nguyên để validation báo lỗi khi publish
      return;
    }

    // Kiểm tra xem có ít nhất 1 section được tick không

    const hasAnySectionSelected = this.model.photoSections.some(section => section.display === true);

    // Cập nhật checkbox chính - chỉ thay đổi nếu giá trị khác
    if (this.model.displayPhotos !== hasAnySectionSelected) {
      this.model.displayPhotos = hasAnySectionSelected;
      // Emit để đảm bảo parent component nhận được thay đổi
      this.modelChange.emit(this.model);
    } else {
    }
  }

  dropSection(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.model.photoSections, event.previousIndex, event.currentIndex);
    // Cập nhật index cho tất cả sections sau khi drag & drop
    // Lưu ý: sectionNumber không thay đổi, chỉ index thay đổi
    this.model.photoSections.forEach((section, index) => {
      section.index = index;
    });
    this.modelChange.emit(this.model);
  }

  moveSectionUp(sectionIndex: number) {
    if (sectionIndex > 0 && this.model.photoSections && this.model.photoSections.length > 1) {
      // Hoán đổi vị trí section với section phía trên
      const temp = this.model.photoSections[sectionIndex];
      this.model.photoSections[sectionIndex] = this.model.photoSections[sectionIndex - 1];
      this.model.photoSections[sectionIndex - 1] = temp;

      // Cập nhật index cho tất cả sections
      // Lưu ý: sectionNumber không thay đổi, chỉ index thay đổi
      this.model.photoSections.forEach((section, index) => {
        section.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  moveSectionDown(sectionIndex: number) {
    if (
      sectionIndex < this.model.photoSections.length - 1 &&
      this.model.photoSections &&
      this.model.photoSections.length > 1
    ) {
      // Hoán đổi vị trí section với section phía dưới
      const temp = this.model.photoSections[sectionIndex];
      this.model.photoSections[sectionIndex] = this.model.photoSections[sectionIndex + 1];
      this.model.photoSections[sectionIndex + 1] = temp;

      // Cập nhật index cho tất cả sections
      // Lưu ý: sectionNumber không thay đổi, chỉ index thay đổi
      this.model.photoSections.forEach((section, index) => {
        section.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  moveSubsectionUp(sectionIndex: number, subIndex: number) {
    const section = this.model.photoSections[sectionIndex];
    if (subIndex > 0 && section && section.subSections && section.subSections.length > 1) {
      // Hoán đổi vị trí subsection với subsection phía trên
      const temp = section.subSections[subIndex];
      section.subSections[subIndex] = section.subSections[subIndex - 1];
      section.subSections[subIndex - 1] = temp;

      // Cập nhật index cho tất cả subsections trong section này
      section.subSections.forEach((subsection, index) => {
        subsection.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  moveSubsectionDown(sectionIndex: number, subIndex: number) {
    const section = this.model.photoSections[sectionIndex];
    if (subIndex < section.subSections.length - 1 && section && section.subSections && section.subSections.length > 1) {
      // Hoán đổi vị trí subsection với subsection phía dưới
      const temp = section.subSections[subIndex];
      section.subSections[subIndex] = section.subSections[subIndex + 1];
      section.subSections[subIndex + 1] = temp;

      // Cập nhật index cho tất cả subsections trong section này
      section.subSections.forEach((subsection, index) => {
        subsection.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  toggleExpandAllPhotoSections() {
    this.allPhotoSectionsExpanded = !this.allPhotoSectionsExpanded;
    this.model.photoSections.forEach(section => {
      section.collapsed = !this.allPhotoSectionsExpanded;
      section.subSections.forEach(subsection => {
        subsection.collapsed = !this.allPhotoSectionsExpanded;
      });
    });
  }

  async uploadPhotosToSubsection(files: FileList, sectionIndex: number, subIndex: number, event?: any) {
    if (!files || files.length === 0) return;

    const subsection = this.model.photoSections[sectionIndex].subSections[subIndex];
    if (!subsection.media) {
      subsection.media = [];
    }

    // Check current total including pending uploads
    const currentTotalCount = this.getTotalPhotoCountInSection(sectionIndex);

    if (currentTotalCount + files.length > this.MAX_IMAGES_PER_SECTION) {
      this.toastr.error(
        `Maximum ${this.MAX_IMAGES_PER_SECTION} photos are allowed per section. You cannot add more photos.`
      );
      return;
    }

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const acceptedImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
      const isHeicByExtension = /\.(heic|heif)$/i.test(file.name);
      const isValidType = acceptedImageTypes.includes(file.type.toLowerCase() || "") || isHeicByExtension;
      if (!isValidType) {
        this.toastr.error(`File ${file.name} format not supported. Accepted formats: JPEG, JPG, PNG, WEBP, HEIC`);
        continue;
      }

      if (file.size / 1024 / 1024 > this.MAX_IMAGE_SIZE_MB) {
        this.toastr.error(
          `File ${file.name} size is ${this.formatFileSize(file.size)}. Maximum ${this.MAX_IMAGE_SIZE_MB}MB per image allowed.`
        );
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      return;
    }

    validFiles.sort((a, b) => a.name.localeCompare(b.name));

    // Add to pending uploads counter BEFORE starting uploads
    this.addPendingUploads(sectionIndex, validFiles.length);

    for (let index = 0; index < validFiles.length; index++) {
      let file = validFiles[index];

      try {
        // Convert HEIC to JPEG for browser preview support (Chrome/Firefox don't display HEIC)
        file = await this.heicConverter.convertToJpegIfNeeded(file);

        const timestamp = Date.now();
        const uploadKey = this.generateUploadKey(
          "photo",
          file.name,
          `section_${sectionIndex}_sub_${subIndex}_${timestamp}_${index}`
        );
        const tempId = `temp_${timestamp}_${index}_${Math.random().toString(36).substr(2, 9)}`;

        this.setUploadState(uploadKey, true);

        const mediaItem = {
          fileName: file.name,
          url: null,
          size: file.size,
          type: "photo",
          uploadKey: uploadKey,
          tempId: tempId,
          isPending: true, // Mark as pending
        };

        subsection.media.push(mediaItem);
        this.cdr.markForCheck();

        await this.uploadSingleFile(file, uploadKey, mediaItem, subsection, sectionIndex, subIndex);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        // Remove from pending counter if upload fails
        this.removePendingUpload(sectionIndex);
      }
    }

    // Sort toàn bộ media theo fileName
    if (subsection.media && subsection.media.length > 1) {
      subsection.media.sort((a, b) => (a.fileName || "").localeCompare(b.fileName || ""));
    }

    this.checkAndSetPhotoDisplay();

    // Reset input để lần sau chọn cùng file vẫn trigger
    if (event && event.target) {
      event.target.value = "";
    }
  }

  private uploadSingleFile(
    file: File,
    uploadKey: string,
    mediaItem: any,
    subsection: any,
    sectionIndex: number,
    subIndex: number
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const subscription = this.uploadService.uploadFileToS3("digital/photos", file, this.MAX_IMAGE_SIZE_MB).subscribe({
        next: (response: any) => {
          if (response && response.type === "progress") {
            this.setUploadProgress(uploadKey, response.percent);
            this.cdr.markForCheck();
            return;
          }

          if (response && response.type === "complete") {
            this.setUploadState(uploadKey, false);
            const mediaIndex = subsection.media.findIndex((media: any) => media.tempId === mediaItem.tempId);
            if (mediaIndex !== -1) {
              subsection.media[mediaIndex] = {
                ...subsection.media[mediaIndex],
                url: response.url,
                uploadKey: null,
                isPending: false, // Remove pending status
              };

              delete subsection.media[mediaIndex].tempId;

              // Remove from pending counter on successful upload
              this.removePendingUpload(sectionIndex);
              this.cdr.markForCheck();

              this.toastr.success(`Photo ${file.name} uploaded successfully`);
              this.cleanupUpload(uploadKey);
              this.activeUploads.delete(uploadKey);
              resolve(response);
            } else {
              console.error(`Could not find media item with tempId: ${mediaItem.tempId}`);
              this.removePendingUpload(sectionIndex);
              this.activeUploads.delete(uploadKey);
              reject(new Error("Media item not found after upload"));
            }
          }
        },
        error: error => {
          this.setUploadState(uploadKey, false);
          const itemIndex = subsection.media.findIndex((media: any) => media.tempId === mediaItem.tempId);
          if (itemIndex > -1) {
            subsection.media.splice(itemIndex, 1);
          }

          // Remove from pending counter on failed upload
          this.removePendingUpload(sectionIndex);
          this.cdr.markForCheck();

          this.toastr.error(`Failed to upload ${file.name}: ${error.message || error}`);
          this.cleanupUpload(uploadKey);
          this.activeUploads.delete(uploadKey);
          reject(error);
        },
      });
      this.activeUploads.set(uploadKey, subscription);
    });
  }

  removePhotoFromSubsection(sectionIndex: number, subIndex: number, mediaIndex: number) {
    const subsection = this.model.photoSections[sectionIndex].subSections[subIndex];
    if (subsection.media[mediaIndex] && subsection.media[mediaIndex].id) {
      this.deleteMedia.push(subsection.media[mediaIndex].id);
      this.deleteMediaChange.emit(this.deleteMedia);
    }
    subsection.media.splice(mediaIndex, 1);
    this.cdr.markForCheck();
    this.modelChange.emit(this.model);
  }

  getPhotoUploadText(subsection: any): string {
    if (!subsection.media || subsection.media.length === 0) {
      return "Select Photos";
    }
    const count = subsection.media.length;
    return count === 1 ? "1 photo selected" : `${count} photos selected`;
  }

  deleteAllPhotosFromSubsection(sectionIndex: number, subIndex: number) {
    if (
      !this.model.photoSections ||
      !this.model.photoSections[sectionIndex] ||
      !this.model.photoSections[sectionIndex].subSections ||
      !this.model.photoSections[sectionIndex].subSections[subIndex]
    ) {
      console.error("Invalid section or subsection index for delete all photos");
      return;
    }

    const subsection = this.model.photoSections[sectionIndex].subSections[subIndex];

    if (!subsection.media || subsection.media.length === 0) {
      this.toastr.info("No photos to delete");
      return;
    }

    const photoCount = subsection.media.length;

    Swal.fire({
      title: "Are you sure?",
      text: `This will delete all ${photoCount} photo(s) from this sub-section.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete all!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        const mediaIdsToDelete = subsection.media
          .filter(media => media.id && typeof media.id === "number" && media.id > 0)
          .map(media => media.id);
        if (mediaIdsToDelete.length > 0) {
          this.deleteMedia = this.deleteMedia.concat(mediaIdsToDelete);
          this.deleteMediaChange.emit(this.deleteMedia);
        }
        subsection.media = [];
        this.cdr.markForCheck();
        this.toastr.success(`All ${photoCount} photo(s) deleted successfully`);
        this.modelChange.emit(this.model);
      }
    });
  }

  getPhotoCountInSection(sectionIndex: number): number {
    if (!this.model.photoSections || !this.model.photoSections[sectionIndex]) {
      return 0;
    }

    let totalPhotos = 0;
    const section = this.model.photoSections[sectionIndex];

    if (section.subSections) {
      section.subSections.forEach(subsection => {
        if (subsection.media && subsection.media.length) {
          totalPhotos += subsection.media.length;
        }
      });
    }

    return totalPhotos;
  }

  /**
   * Get total count including pending uploads
   */
  getTotalPhotoCountInSection(sectionIndex: number): number {
    const currentPhotos = this.getPhotoCountInSection(sectionIndex);
    const pendingUploads = this.pendingUploadsPerSection[sectionIndex] || 0;
    return currentPhotos + pendingUploads;
  }

  /**
   * Initialize pending uploads counter for section
   */
  private initializePendingCounter(sectionIndex: number): void {
    if (this.pendingUploadsPerSection[sectionIndex] === undefined) {
      this.pendingUploadsPerSection[sectionIndex] = 0;
    }
  }

  /**
   * Add to pending uploads counter
   */
  private addPendingUploads(sectionIndex: number, count: number): void {
    this.initializePendingCounter(sectionIndex);
    this.pendingUploadsPerSection[sectionIndex] += count;
  }

  /**
   * Remove from pending uploads counter
   */
  private removePendingUpload(sectionIndex: number): void {
    this.initializePendingCounter(sectionIndex);
    if (this.pendingUploadsPerSection[sectionIndex] > 0) {
      this.pendingUploadsPerSection[sectionIndex]--;
    }
  }

  /**
   * Get pending uploads count for section (for template use)
   */
  getPendingUploadsInSection(sectionIndex: number): number {
    return this.pendingUploadsPerSection[sectionIndex] || 0;
  }

  isPhotoSectionAtMaxCapacity(sectionIndex: number): boolean {
    return this.getTotalPhotoCountInSection(sectionIndex) >= this.MAX_IMAGES_PER_SECTION;
  }
  validatePhotoSectionLimits(): string[] {
    const errors: string[] = [];
    if (this.model.photoSections) {
      this.model.photoSections.forEach((section: any, index: number) => {
        const totalPhotoCount = this.getTotalPhotoCountInSection(index);
        const currentPhotos = this.getPhotoCountInSection(index);
        const pendingUploads = this.pendingUploadsPerSection[index] || 0;

        if (totalPhotoCount > this.MAX_IMAGES_PER_SECTION) {
          let errorMessage = `Section ${index + 1}: ${totalPhotoCount}/${this.MAX_IMAGES_PER_SECTION} images (Max exceeded)`;
          if (pendingUploads > 0) {
            errorMessage += ` - ${currentPhotos} uploaded + ${pendingUploads} pending`;
          }
          errors.push(errorMessage);
        }
      });
    }
    return errors;
  }

  showPhotoLimitErrors(): void {
    const errors = this.validatePhotoSectionLimits();
    if (errors.length > 0) {
      errors.forEach(error => {
        this.toastr.error(error, "Photo Limit Exceeded");
      });
    }
  }

  public validateAndShowErrors(): boolean {
    const errors = this.validatePhotoSectionLimits();
    if (errors.length > 0) {
      errors.forEach(error => {
        this.toastr.error(error, "Cannot Submit - Photo Limit Exceeded");
      });
      return false;
    }
    return true;
  }

  public hasPhotoLimitErrors(): boolean {
    return this.validatePhotoSectionLimits().length > 0;
  }

  ngOnChanges(): void {
    if (this.model && this.model.photoSections) {
      // Ensure display field has default value
      this.model.photoSections.forEach((section, index) => {
        if (section.display === undefined) {
          section.display = true;
        }
        if (section.index === undefined) {
          section.index = index;
        }
        if (section.subSections) {
          section.subSections.forEach((subsection, subIndex) => {
            if (subsection.display === undefined) {
              subsection.display = true;
            }
            if (subsection.index === undefined) {
              subsection.index = subIndex;
            }
          });
        }
      });

      const errors = this.validatePhotoSectionLimits();
      if (errors.length > 0) {
        console.warn("Photo section limits exceeded:", errors);
      }
    }
  }

  onPreviewClick(media: any, type: string) {
    if (media.url) {
      window.open(media.url, "_blank");
    }
  }

  trackByIndex(index: number, item: any): any {
    return index;
  }

  scrollToPhotoSection(sectionIndex: number): void {
    const el = document.getElementById(`photo-section-${sectionIndex}`);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /** Stable trackBy for media - avoids re-renders when list changes */
  trackByMedia(index: number, media: any): string | number {
    if (media.id != null) return media.id;
    if (media.tempId) return media.tempId;
    if (media.url) return media.url;
    return "media-" + index;
  }

  /** Chunk media into rows for virtual scroll - only visible rows in DOM */
  getMediaRows(subsection: any): any[][] {
    const media = subsection.media || [];
    const rows: any[][] = [];
    for (let i = 0; i < media.length; i += PHOTO_GRID_COLUMNS) {
      rows.push(media.slice(i, i + PHOTO_GRID_COLUMNS));
    }
    return rows;
  }

  trackByMediaRow(index: number, row: any[]): string {
    const first = row[0];
    const firstId = first ? first.tempId || first.id || first.url || "m-" + index : "row-" + index;
    return "row-" + index + "-" + firstId;
  }

  useVirtualScroll(subsection: any): boolean {
    return subsection.media && subsection.media.length > VIRTUAL_SCROLL_THRESHOLD;
  }

  getPhotoViewportHeight(): number {
    if (typeof window === "undefined") return 300;
    return Math.min(400, Math.max(250, window.innerHeight * 0.35));
  }

  // Helper methods
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  private generateUploadKey(prefix: string, fileName: string, suffix?: string): string {
    const base = `${prefix}_${Date.now()}_${fileName}`;
    return suffix ? `${base}_${suffix}` : base;
  }

  private setUploadState(key: string, isUploading: boolean) {
    this.uploadStates[key] = isUploading;
  }

  private setUploadProgress(key: string, percent: number) {
    this.uploadProgress[key] = percent;
  }

  isUploadInProgress(key: string): boolean {
    return this.uploadStates[key] || false;
  }

  getProgressByKey(key: string): number {
    return this.uploadProgress[key] || 0;
  }

  private cleanupUpload(key: string) {
    delete this.uploadProgress[key];
    delete this.uploadStates[key];
  }

  ngOnDestroy(): void {
    this.activeUploads.forEach((subscription, uploadKey) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
      this.cleanupUpload(uploadKey);
    });
    this.activeUploads.clear();
  }
}
